import React, { useMemo, useState } from "react";
import { makeStyles, Dialog, DialogTitle, DialogContent, FormControl, Tooltip, InputLabel, Select, MenuItem, Typography, Hidden, GridList, GridListTile, Grid, Button, DialogActions, CircularProgress } from "@material-ui/core";
import InfoIcon from "@material-ui/icons/Info";
import UploadIcon from "@material-ui/icons/CloudUploadOutlined";

import classNames from "classnames";

import BackgroundImages from "../../../../resources/BackgroundImages.json";
import { BuilderAssetService, SaveCustomSlideBackgroundImageRequest } from "../../../../services/BuilderAssetService";
import { BuilderAsset } from "../../../../entities/BuilderAsset";
import { AspectRatios } from "../../../../entities/AspectRatios";
import { LoadingPage } from "../../../../components/LoadingPage";
import { HorizontalCentered } from "../../../../components/HorizontalCentered";
import { VerticalCenter } from "../../../../components/VerticalCenter";

const useStyles = makeStyles((theme) => ({
	fillParent: {
		width: "100%",
		height: "100%",
	},
	gridList: {
		flexWrap: "nowrap",
		// Promote the list into his own layer on Chrome. This cost memory but helps keeping high FPS.
		transform: "translateZ(0)",
		overflowX: "scroll",
	},
	imageSelection: {
		marginTop: 10,
		display: "flex",
		flexWrap: "wrap",
		overflow: "hidden",
		backgroundColor: theme.palette.background.paper,
	},
	gridTileImage: {
		margin: 3,
		"&:hover": {
			"& img": {
				filter: "blur(1px) contrast(80%)",
				transition: "all 0.2s ease 0s",
				MozTransition: "all 0.2s ease 0s",
				WebkitTransition: "all 0.2s ease 0s",
			},
		},
	},
	selected: {
		border: "2px solid #2196f3",
	},
	uploadImageContainer: {
		height: "100%",
		backgroundColor: "#f7f7f7",
		"&:hover": {
			"& svg": {
				color: "#2196f3",
			},
			"& p": {
				color: "#2196f3",
			},
		},
	},
	uploadInput: {
		display: "none",
	},
	uploadButton: {
		"&:hover": {
			backgroundColor: "rgba(0, 0, 0, 0.04)",
		},
	},
	uploadIcon: {
		color: "rgba(0, 0, 0, 0.68)",
		fontSize: 30,
	},
	customImage: {
		margin: 5,
		maxHeight: 275,
	},
	removeImageButton: {
		color: theme.palette.error.main,
	},
}));

const images: { id: number; standardImageThumbnailPath: string; widescreenImageThumbnailPath: string; name: string }[] = BackgroundImages as any;

const slideAspectRatios = [
	{
		id: "16x9",
		name: "16 x 9",
	},
	{
		id: "4x3",
		name: "4 x 3",
	},
];

interface Props {
	open: boolean;
	preferredAspectRatio: AspectRatios;
	preferredImageId: number;
	hasPreferredCustomImage: boolean;
	onClose: () => void;
	onSave: (selectedAspectRatio: AspectRatios, selectedImageId: number, uploadedCustomImage: boolean) => void;
	onCustomBackgroundSave: (asset: BuilderAsset, customImageAddedClient64Base: string, customImageAddedClientMimeType: string) => void;
	id: string;
	backgroundImageAspectRatio?: string;
	customSlideBackgroundImagePath?: string;
	onDeleteCustomSave: () => void;
	customImageBase64Content?: string;
}

export function SlideSettingsDialog(props:Props){
	const classes = useStyles();

	const [loading, setLoading] = useState(false);
	const [selectedAspectRatioId, setSelectedAspectRatioId] = useState<AspectRatios>(props.preferredAspectRatio);
	const [selectedImageId, setSelectedImageId] = useState(props.preferredImageId);
	const [hasPreferredCustomImage, setHasPreferredCustomImage] = useState(props.hasPreferredCustomImage);
	const imageFullPath = useMemo(() => `${props.customSlideBackgroundImagePath}`, [props.customSlideBackgroundImagePath]);

	const saveSettings = (event: React.FormEvent<HTMLFormElement>) => {
		setLoading(true);
		event.preventDefault();
		props.onSave(selectedAspectRatioId, selectedImageId, hasPreferredCustomImage);
		setLoading(false);
	};

	const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		let file = event.target.files && event.target.files[0];
		if (file) {
			handleFileUpload(file);
		}
	};

	const handleFileUpload = (file: File) => {
		const reader = new FileReader();
		reader.readAsDataURL(file);

		reader.onload = async () => {
			const result = reader.result;
			if (result) {
				// result type changes based on which read function was used
				const selectedCustomImage = result as string;
				
				let fileName = "master-background";
				let uploadedFileNameSplit = file.name.split(".");
				let extension = uploadedFileNameSplit[uploadedFileNameSplit.length - 1];
				fileName = fileName + `-${+new Date()}.${extension}`;
				
				setLoading(true);
				let uploadUrlResponse = await BuilderAssetService.generatePowerPointUploadUrl(props.id, fileName);
				setLoading(false);
				
				// What error we want to throw?
				if (uploadUrlResponse.error) return;
				setLoading(true);
				let uploadResponse = await BuilderAssetService.uploadSourceImage(
					uploadUrlResponse.data.url,
					file,
					(uploadProgressPercentage) => null
					);
				setLoading(false);

				// What error we want to throw?
				if (uploadResponse.error) return;

				let request: SaveCustomSlideBackgroundImageRequest = {
					fileContents: selectedCustomImage,
					id: props.id,
				};
				setLoading(true);
				let response = await BuilderAssetService.saveSlideExporterCustomBackgroundImage(request);
				setLoading(false);

				if (response.success) {
					props.onCustomBackgroundSave(response.data, selectedCustomImage, file.type);
					return setHasPreferredCustomImage(true);
				} 
			};
		};
	};

	const onRemoveImage = async () => {
		setLoading(true);
		await BuilderAssetService.removeCustomSlideBackgroundImageUrl(props.id);
		props.onDeleteCustomSave();
		setHasPreferredCustomImage(false);
		setLoading(false);
	};

	return (
		<Dialog maxWidth="sm" fullWidth={true} open={props.open} onClose={props.onClose}>
			<DialogTitle>Slide Settings</DialogTitle>
			<form onSubmit={(e) => saveSettings(e)}>
				<DialogContent style={{ paddingTop: 0 }}>
					<FormControl fullWidth margin="normal">
						<Tooltip title="Choose Your Aspect Ratio" placement="top">
							<InputLabel htmlFor="AspectRatio" style={{ fontSize: 21 }}>
								Aspect Ratio <InfoIcon style={{ fontSize: 17 }} />
							</InputLabel>
						</Tooltip>
						<Select
							disabled={loading}
							inputProps={{
								name: "AspectRatio",
								id: "AspectRatio",
							}}
							value={selectedAspectRatioId}
							onChange={(event) => setSelectedAspectRatioId(event.target.value as AspectRatios)}
						>
							{slideAspectRatios.map((aspectRatio, index) => (
								<MenuItem value={aspectRatio.id} selected={selectedAspectRatioId === aspectRatio.id} key={index}>
									{aspectRatio.name}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<div>
						{selectedAspectRatioId === "16x9" && !hasPreferredCustomImage && (
							<>
								<Typography variant="subtitle2">Select A Background Image</Typography>
								<Hidden lgUp>
									{ loading
										? 
										<VerticalCenter>
											<HorizontalCentered>
												<CircularProgress />
											</HorizontalCentered>
										</VerticalCenter>
										:
										<GridList
											className={classNames("customScrollbar", "customScrollbarHorizontal", classes.gridList)}
											cellHeight="auto"
											cols={1.2}
											spacing={6}
										>
											<UploadImageTile onFileChange={onFileChange} />
											{images.map((image) => (
												<GridListTile
													key={image.id}
													onClick={() => setSelectedImageId(image.id)}
													onDoubleClick={() => props.onSave(selectedAspectRatioId, image.id, hasPreferredCustomImage)}
													className={classNames(
														classes.gridTileImage,
														image.id === selectedImageId ? classes.selected : null
													)}
												>
													<img src={image.widescreenImageThumbnailPath} alt={image.name} />
												</GridListTile>
											))}
										</GridList>
									}
								</Hidden>
								<Hidden mdDown>
									{ loading
										? 
										<VerticalCenter>
											<HorizontalCentered>
												<CircularProgress />
											</HorizontalCentered>
										</VerticalCenter>
										:
										<GridList
											className={classNames("customScrollbar", "customScrollbarHorizontal", classes.gridList)}
											cellHeight="auto"
											cols={2.5}
											spacing={6}
										>
											<UploadImageTile onFileChange={onFileChange} />
											{images.map((image) => (
												<GridListTile
													key={image.id}
													onClick={() => setSelectedImageId(image.id)}
													onDoubleClick={() => props.onSave(selectedAspectRatioId, image.id, hasPreferredCustomImage)}
													className={classNames(
														classes.gridTileImage,
														image.id === selectedImageId ? classes.selected : null
													)}
												>
													<img src={image.widescreenImageThumbnailPath} alt={image.name} />
												</GridListTile>
											))}
										</GridList>
										}
								</Hidden>
							</>
						)}
						{selectedAspectRatioId === "4x3" && !hasPreferredCustomImage && (
							<>
								<Typography variant="subtitle2">Select A Background Image</Typography>
								<Hidden lgUp>
									{ loading
										? 
										<VerticalCenter>
											<HorizontalCentered>
												<CircularProgress />
											</HorizontalCentered>
										</VerticalCenter>
										:
										<GridList
											className={classNames("customScrollbar", "customScrollbarHorizontal", classes.gridList)}
											cellHeight="auto"
											cols={1.2}
										>
											<UploadImageTile onFileChange={onFileChange} />
											{images.map((image) => (
												<GridListTile
												key={image.id}
												onClick={() => setSelectedImageId(image.id)}
													onDoubleClick={() => props.onSave(selectedAspectRatioId, image.id, hasPreferredCustomImage)}
													className={classNames(
														classes.gridTileImage,
														image.id === selectedImageId ? classes.selected : null
														)}
												>
													<img src={image.standardImageThumbnailPath} alt={image.name} />
												</GridListTile>
											))}
										</GridList>
									}
								</Hidden>
								<Hidden mdDown>
									{ loading
										?
										<VerticalCenter>
											<HorizontalCentered>
												<CircularProgress />
											</HorizontalCentered>
										</VerticalCenter>
										:
										<GridList
											className={classNames("customScrollbar", "customScrollbarHorizontal", classes.gridList)}
											cellHeight="auto"
											cols={2.8}
										>
											<UploadImageTile onFileChange={onFileChange} />
											{images.map((image) => (
												<GridListTile
													key={image.id}
													onClick={() => setSelectedImageId(image.id)}
													onDoubleClick={() => props.onSave(selectedAspectRatioId, image.id, hasPreferredCustomImage)}
													className={classNames(
														classes.gridTileImage,
														image.id === selectedImageId ? classes.selected : null
													)}
												>
													<img src={image.standardImageThumbnailPath} alt={image.name} />
												</GridListTile>
											))}
										</GridList>
									}
								</Hidden>
							</>
						)}
						{hasPreferredCustomImage && (
							<>
								<Typography variant="subtitle2" align="left">
									Selected Background Image
								</Typography>
								<img
									alt="Slide Background"
									src={ props.customImageBase64Content ? `${props.customImageBase64Content}` : imageFullPath }
									className={classes.customImage}
								/>
								<Grid container justifyContent="center">
									<Grid item>
										<Button variant="text" className={classes.removeImageButton} onClick={onRemoveImage}>
											Remove Image
										</Button>
									</Grid>
								</Grid>
							</>
						)}
					</div>
				</DialogContent>
				<DialogActions>
					<Button onClick={props.onClose} color="primary" disabled={loading}>
						Cancel
					</Button>
					<Button type="submit" color="primary" disabled={loading}>
						Apply Settings
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
}

interface UploadImageTileProps {
	onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

function UploadImageTile(props: UploadImageTileProps){
	const classes = useStyles()
	return (
		<GridListTile>
			<Grid container alignContent="center" justifyContent="center" direction="column" className={classes.uploadImageContainer}>
				<label htmlFor="file-upload-input" className={classes.fillParent}>
					<Button component="span" className={classNames(classes.uploadButton, classes.fillParent)} variant="text">
						<Grid item>
							<Grid container justifyContent="center" alignContent="center" direction="row">
								<Grid item>
									<UploadIcon className={classes.uploadIcon} />
								</Grid>
							</Grid>
							<Typography align="center">Upload Image</Typography>
						</Grid>
					</Button>
				</label>
			</Grid>
			<input className={classes.uploadInput} id="file-upload-input" onChange={(e) => props.onFileChange(e)} type="file" accept="image/*" />
		</GridListTile>
	)
}